/* eslint-disable react/jsx-no-target-blank */
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { numericalInputStyles } from '../../styles/mainStyle';
import { AspectRatio } from '../../types.ts/video';
import { percentOr } from '../../utility/panelFieldValues';
import PanelSliderField from '../common/PanelSliderField';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import ImageFitPropertySelect from './ImageFitPropertySelect';
import { ImagePreset } from './ImagePreset';
import PanelSection, { FieldRow } from './PanelSection';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface ImageSettingsProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
  onReplaceImage?: (newUrl: string) => void;
  parentCompositionElement?: ElementState;
}
const DEFAULT_FIT_PROPS = [
  { caption: 'Cover', value: 'cover' },
  { caption: 'Contain', value: 'contain' },
  {
    caption: 'Black Frame',
    value: 'black-frame',
  },
];

export const ImageSettings: React.FC<ImageSettingsProps> = props => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = props;
  const [imageFitProps, setImageFitProps] = useState(DEFAULT_FIT_PROPS);

  // Update image element properties using headless editor
  const updateImageProperty = async (propertyUpdates: Record<string, any>) => {
    if (!props.activeElement?.source?.id) return;
    editorStore.editor.commandUpdateElement(
      props.activeElement.source.id,
      propertyUpdates,
    );
  };

  // Sliders fire per value while dragged: one command (one undo entry) per
  // drag, as in the video settings.
  const commandUpdateElementDebounced = useCallback(
    debounce((elementId: string, propertyUpdates: Record<string, unknown>) => {
      editorStore.editor.commandUpdateElement(elementId, propertyUpdates);
    }, 250),
    [editorStore.editor],
  );
  const updateImageSliderProperty = (
    propertyUpdates: Record<string, unknown>,
  ) => {
    if (!props.activeElement?.source?.id) return;
    commandUpdateElementDebounced(
      props.activeElement.source.id,
      propertyUpdates,
    );
  };

  useEffect(() => {
    const aspectRatio = videoCreator.currentVideo?.aspectRatio;
    if (
      aspectRatio === AspectRatio.AR_9_16 ||
      aspectRatio === AspectRatio.AR_1_1
    ) {
      setImageFitProps(imageFitProps.filter(f => f.value !== 'black-frame'));
    } else setImageFitProps(DEFAULT_FIT_PROPS);
  }, [videoCreator.currentVideo?.aspectRatio]);

  return (
    <ImageSettingsContainer>
      <ImagePresetContainer>
        <ImagePreset
          activeElement={props.activeElement}
          url={props.activeElement.source.source}
          onSourceChange={props.onReplaceImage}
          parentCompositionElement={props.parentCompositionElement}
        />
      </ImagePresetContainer>

      <PanelSection title="Timing">
        <FieldRow>
          <div>
            <PropertyCaption>Start</PropertyCaption>
            <PropertyNumericalInput
              activeElement={props.activeElement}
              propertyName="time"
              defaultValue="auto"
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateImageProperty({ [property]: value });
              }}
            />
          </div>
          <div>
            <PropertyCaption>Duration</PropertyCaption>
            <PropertyNumericalInput
              activeElement={props.activeElement}
              propertyName="duration"
              defaultValue="auto"
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                // Update composition inner elements' duration using commandUpdateElement
                if (
                  videoCreator.isImageElementComposition(props.activeElement)
                ) {
                  for (const e of props.activeElement.elements || []) {
                    editorStore.editor.commandUpdateElement(e.source.id, {
                      duration: parseFloat(value),
                    });
                  }
                }

                // Update the main property using headless editor
                await updateImageProperty({ [property]: value });
              }}
            />
          </div>
        </FieldRow>
      </PanelSection>

      <PanelSection title="Layout">
        <PropertyCaption>Fit</PropertyCaption>
        <ImageFitPropertySelect
          activeElement={props.activeElement}
          propertyName="fit"
          defaultValue="cover"
          options={imageFitProps}
          editorStore={editorStore}
        />
        <PanelSliderField
          label="Opacity"
          value={percentOr(props.activeElement.source.opacity, 100)}
          min={0}
          max={100}
          snapTo={[100]}
          onChange={newValue =>
            updateImageSliderProperty({ opacity: `${newValue}%` })
          }
        />
      </PanelSection>

      {props.activeElement.source.type === 'image' && (
        <PanelSection title="Position">
          <PanelSliderField
            label="Horizontal Alignment"
            value={percentOr(props.activeElement.source.x_alignment, 50)}
            min={0}
            max={100}
            snapTo={[50]}
            onChange={newValue =>
              updateImageSliderProperty({ x_alignment: newValue + '%' })
            }
          />
          <PanelSliderField
            label="Vertical Alignment"
            value={percentOr(props.activeElement.source.y_alignment, 50)}
            min={0}
            max={100}
            snapTo={[50]}
            onChange={newValue =>
              updateImageSliderProperty({ y_alignment: newValue + '%' })
            }
          />
          <PanelSliderField
            label="Zoom"
            value={percentOr(props.activeElement.source.x_scale, 100)}
            min={100}
            max={150}
            onChange={newValue =>
              updateImageSliderProperty({
                x_scale: newValue + '%',
                y_scale: newValue + '%',
              })
            }
          />
        </PanelSection>
      )}

      <PanelSection title="Animation">
        <ElementAnimationSettings
          activeElement={props.activeElement}
          editorStore={editorStore}
        />
        <GenericAnimationSettings
          activeElement={props.activeElement}
          editorStore={editorStore}
        />
      </PanelSection>
    </ImageSettingsContainer>
  );
};

// const Information = styled.div`
//   margin-top: 20px;
//   color: #a3a5a5;

//   a {
//     color: #a3a5a5;
//   }
// `;

const ImageSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  padding-bottom: 20px;
  box-sizing: border-box;
`;

const ImagePresetContainer = styled.div`
  margin: var(--spacing-md) 0 var(--spacing-lg);
`;
